import { useEffect, useState } from "react";
import {
  CheckCircle2,
  ChevronRight,
  Dot,
  Plus,
  RefreshCcw,
} from "lucide-react";
import { useParams } from "react-router-dom";
import { usePlanVersionList, usePlanVersionDetail } from "../../../hooks";
import { ValueLabel } from "@features/ax/shared/ValueLabel";

const snapshotItems = [
  { key: "rateType", snakeKey: "rates", label: "Rate Type" },
  { key: "services", snakeKey: "services", label: "Services" },
  { key: "minBill", snakeKey: "minBill", label: "Min Bill" },
  { key: "autoPay", snakeKey: "autoPay", label: "Auto Pay" },
  { key: "deposit", snakeKey: "deposit", label: "Deposit" },
  { key: "penaltyRules", snakeKey: "penaltyRules", label: "Penalty Rules" },
];

export default function PlanVersionHistory() {
  const { id } = useParams();
  const { data: versionListData } = usePlanVersionList(id || "");

  const versions = versionListData?.result?.versionsList || [];

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);

  useEffect(() => {
    if (versions.length > 0 && !selectedVersionId) {
      setSelectedVersionId(versions[0].id.toString());
    }
  }, [versions, selectedVersionId]);

  const { data: versionDetailData, isFetching } = usePlanVersionDetail(id || "", selectedVersionId || "");

  const detail = versionDetailData?.result?.versionDetail || versionListData?.result?.versionDetail;
  const metadata = detail?.metadata || {};
  const whatChanged = detail?.whatChanged || { changes: [] };

  const activeVersion = versions.find((v: any) => v.isCurrent) || versions[0];

  return (
    <div className="min-h-screen p-6">
      <div className="grid grid-cols-12 gap-6">
        {/* LEFT SIDEBAR */}
        <div className="col-span-12 lg:col-span-3">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-900">
                Versions
              </h2>
              {activeVersion && (
                <div className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600">
                  {activeVersion.version} active
                </div>
              )}
            </div>
            <div className="relative ml-4 border-l border-slate-200">
              {versions.map((item: any) => {
                const selected = selectedVersionId === item.id.toString();
                return (
                  <button
                    key={item.id}
                    onClick={() => setSelectedVersionId(item.id.toString())}
                    className={`group relative mb-4 block w-full rounded-xl border p-4 text-left transition-all ${selected
                        ? "border-blue-200 bg-blue-50"
                        : "border-transparent hover:border-slate-200 hover:bg-slate-50"
                      }`}
                  >
                    {/* Timeline Dot */}
                    <div
                      className={`absolute -left-[23px] top-8 h-5 w-5 rounded-full border-4 border-white ${selected ? "bg-slate-800" : "bg-blue-500"
                        }`}
                    />
                    <div className="flex items-center gap-3">
                      <h3 className="text-sm font-bold text-slate-900">
                        {item.version}
                      </h3>
                      {item.isCurrent && (
                        <div className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-0.5 text-xs font-semibold text-emerald-700">
                          Active
                        </div>
                      )}
                    </div>
                    <p className="mt-2 text-xs text-slate-500">
                      {new Date(item.startDate).toLocaleDateString()}
                    </p>
                    <p className="mt-1 text-xs text-slate-600">
                      {item.shortDescription}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        {/* RIGHT CONTENT */}
        <div className="col-span-12 space-y-5 lg:col-span-9">
          {/* HEADER */}
          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-xl font-bold tracking-tight text-slate-900">
                    {metadata.version || "Select a version"}
                  </h1>
                  {metadata.isCurrent && (
                    <div className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-0.5 text-xs font-semibold text-emerald-700">
                      Active
                    </div>
                  )}
                  {metadata.replacesVersion && (
                    <div className="text-xs text-slate-500">
                      replaces{" "}
                      <span className="font-semibold text-slate-700">
                        {metadata.replacesVersion}
                      </span>
                    </div>
                  )}
                </div>
                <p className="mt-2 text-sm text-slate-600">
                  {metadata.shortDescription}
                </p>
              </div>
              {metadata.version && (
                <div className="flex gap-10">
                  <div>
                    <p className="text-[12px] font-semibold tracking-[0.15em] text-slate-400">
                      Effective
                    </p>
                    <p className="mt-1 text-base font-semibold text-slate-900">
                      {metadata.effectiveDate || "NA"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[12px] font-semibold tracking-[0.15em] text-slate-400">
                      Author
                    </p>
                    <p className="mt-1 text-base font-semibold text-slate-900">
                      {metadata.author || "NA"}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* CHANGES */}
          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            {isFetching ? (
              <div className="text-sm text-slate-500">Loading details...</div>
            ) : (
              <>
                <div className="mb-6 flex items-center justify-between">
                  <h2 className="text-base font-bold text-slate-900">
                    What changed{" "}
                    {metadata.replacesVersion
                      ? `vs ${metadata.replacesVersion}`
                      : "in this version"}
                  </h2>
                  <div className="rounded-full border border-blue-200 bg-blue-50 px-3 py-0.5 text-xs font-semibold text-blue-600">
                    {whatChanged.changes?.length || 0} change{(whatChanged.changes?.length || 0) !== 1 ? "s" : ""}
                  </div>
                </div>
                <div className="space-y-3">
                  {whatChanged.changes?.length === 0 && (
                    <div className="text-xs text-slate-500">No changes recorded for this version.</div>
                  )}
                  {whatChanged.changes?.map((change: any, index: number) => {
                    const changeType = change.changeType || change.change_type || "update";
                    const fieldDisplay = change.entityName || change.field_display || change.field;
                    const oldValue = change.oldValue !== undefined ? change.oldValue : change.from_value;
                    const newValue = change.newValue !== undefined ? change.newValue : change.to_value;
                    const isAdded = changeType.toLowerCase() === "added" || changeType.toLowerCase() === "add";

                    return (
                      <div
                        key={index}
                        className={`rounded-lg border p-4 ${isAdded
                            ? "border-emerald-200 bg-emerald-50"
                            : "border-slate-200 bg-slate-50"
                          }`}
                      >
                        <div className="flex flex-wrap items-center gap-3">
                          <div
                            className={`rounded-full border px-3 py-0.5 text-sm font-semibold ${isAdded
                                ? "border-emerald-200 bg-emerald-100 text-emerald-700"
                                : "border-blue-200 bg-blue-100 text-blue-700"
                              }`}
                          >
                            {changeType}
                          </div>
                          {(change.category || change.category_name) && (
                            <div className="bg-slate-200 px-2 py-0.5 text-[10px] font-bold tracking-[0.15em] text-slate-500">
                              {change.category || change.category_name}
                            </div>
                          )}
                          <div className="text-sm font-semibold text-slate-900">
                            {fieldDisplay}
                          </div>
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                          {!isAdded && oldValue !== undefined && oldValue !== null && (
                            <>
                              <div className="bg-slate-200 px-2 py-0.5 text-[10px] font-bold tracking-[0.15em] text-slate-500">
                                From
                              </div>
                              <span className="text-red-500 line-through">
                                {oldValue}
                              </span>
                              <ChevronRight className="h-3 w-3 text-slate-400" />
                            </>
                          )}
                          <div className="bg-slate-200 px-2 py-0.5 text-[10px] font-bold tracking-[0.15em] text-slate-500">
                            To
                          </div>
                          <span className="font-medium text-emerald-700">
                            {newValue}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
